import React from 'react';
import Card from './Card';

interface ResponsiveTableProps {
  headers: string[];
  rows: any[][];
  renderRow: (row: any[], index: number) => React.ReactNode;
  renderMobileCard?: (row: any[], index: number) => React.ReactNode;
  emptyMessage?: string;
  className?: string;
}

const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
  headers,
  rows,
  renderRow,
  renderMobileCard,
  emptyMessage = 'No data available',
  className = ''
}) => {
  // Default mobile card renderer if not provided
  const defaultMobileCard = (row: any[], index: number) => (
    <Card key={index} className="mb-4">
      <div className="px-4 py-5 sm:p-6">
        <div className="grid grid-cols-1 gap-4">
          {headers.map((header, headerIndex) => (
            <div key={headerIndex} className="flex justify-between items-center">
              <dt className="text-sm font-medium text-gray-500 flex-1">{header}</dt>
              <dd className="text-sm text-gray-900 ml-4 text-right">
                {typeof row[headerIndex] === 'string' || typeof row[headerIndex] === 'number'
                  ? row[headerIndex]
                  : row[headerIndex]}
              </dd>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );

  const mobileCardRenderer = renderMobileCard || defaultMobileCard;

  return (
    <div className={className}>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={headers.length} className="px-6 py-4 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => renderRow(row, index))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden">
        {rows.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {emptyMessage}
          </div>
        ) : (
          rows.map((row, index) => mobileCardRenderer(row, index))
        )}
      </div>
    </div>
  );
};

export default ResponsiveTable;